import { acceptJuditConsulta, isGuid, juditRefreshWait, juditUncertain, normalizeCnj, validCnj } from '../legal-rag/juditDocumentHelpers.js'

export const PROCESS_SYNC_DOCUMENT_LIMIT = 40
const UNKNOWN_STATES = ['ENVIO_INCERTO', 'BAIXANDO', 'EXTRAINDO']

export class ProcessDocumentSyncError extends Error {
  constructor(message, { code = '', uncertain = false, receipt = null } = {}) {
    super(message)
    this.name = 'ProcessDocumentSyncError'
    this.code = code
    this.uncertain = uncertain
    this.receipt = receipt
  }
}

export function processSyncError(response) {
  if (response?.code === 'JUDIT_INSTANCE_REQUIRED') return 'Selecione o grau do processo antes de sincronizar. Nenhuma nova consulta foi iniciada.'
  if (response?.code === 'JUDIT_INSTANCE_CONFLICT') return 'O grau informado diverge da consulta selecionada. Confira a seleção antes de sincronizar.'
  if ([401, 403].includes(response?.status)) return 'Sua sessão ou permissão não permite sincronizar estes documentos.'
  if (response?.status === 429 || response?.code === 'JUDIT_UPDATE_INTERVAL') return 'Aguarde o intervalo informado antes de continuar a sincronização.'
  if (response?.status === 409) return 'O estado ou a versão mudou. Releia os documentos locais antes de continuar.'
  return 'A sincronização não foi confirmada. Confira o recibo e reconcilie a operação pelos dados locais antes de qualquer novo envio.'
}

export function processSyncProgress(consulta, phase, extra = {}) {
  const rows = consulta?.anexos || []
  return { phase, instancia: consulta?.instancia || null, total: rows.length, stored: rows.filter((row) => isGuid(row.documentoId)).length,
    extracted: rows.filter((row) => row.estado === 'EXTRAIDO').length,
    failures: rows.filter((row) => ['ERRO', 'OCR_NECESSARIO', 'LIMITE_EXCEDIDO'].includes(row.estado)).length,
    remaining: rows.filter((row) => row.podeBaixar && row.estado === 'DISPONIVEL').length,
    unavailable: rows.filter((row) => row.estado === 'INDISPONIVEL').length, ...extra }
}

export function processSyncRemotePending(consulta) {
  return ['CRIADA', 'PROCESSANDO', 'ENVIO_INCERTO'].includes(consulta?.estado) ||
    ['pending', 'started', 'processing', 'cancelling'].includes(String(consulta?.estadoJudit || '').toLowerCase())
}

export function processSyncUnknown(consulta) {
  return consulta?.estado === 'ENVIO_INCERTO' || consulta?.anexos?.some((row) => UNKNOWN_STATES.includes(row.estado))
}

export function processSyncReceiptForGrade(receipts, instancia, activeRequestId = '') {
  return instancia ? receipts.find((receipt) => receipt.instancia === instancia) || null
    : receipts.find((receipt) => receipt.requestId === activeRequestId && !['FINISHED', 'FAILED'].includes(receipt.state)) || null
}

export function processSyncTarget(consulta, explicitInstance = null) {
  const instancia = [1, 2].includes(explicitInstance) ? explicitInstance : consulta?.instancia || null
  return { instancia, consulta: consulta?.instancia === instancia ? consulta : null }
}

export function processSyncStorageKey(actorId, numeroProcesso, instancia = null) {
  if (!Number.isSafeInteger(Number(actorId)) || Number(actorId) <= 0 || !validCnj(numeroProcesso)) return ''
  if (instancia != null && ![1, 2].includes(instancia)) return ''
  return `veiga-law:judit-doc-sync:v1:${Number(actorId)}:${normalizeCnj(numeroProcesso)}:${instancia || 'pendente'}`
}

export function readProcessSyncReceipt(storage, key) {
  if (!key) return null
  try {
    const raw = storage.getItem(key)
    if (!raw || raw.length > 4096) return null
    const receipt = JSON.parse(raw)
    if (!isGuid(receipt.requestId) || receipt.consultaId != null && !isGuid(receipt.consultaId) ||
      receipt.instancia != null && ![1, 2].includes(receipt.instancia) ||
      receipt.anexoId != null && !isGuid(receipt.anexoId) || receipt.versaoAnterior != null && !isGuid(receipt.versaoAnterior) ||
      !['REQUESTING', 'KNOWN', 'WAITING', 'UNCERTAIN', 'FAILED', 'FINISHED'].includes(receipt.state)) return null
    return receipt
  } catch { return null }
}

export function writeProcessSyncReceipt(storage, key, receipt) {
  if (!key) throw new ProcessDocumentSyncError('Não foi possível identificar sua sessão para guardar o recibo. Nenhuma solicitação foi enviada.', { code: 'LOCAL_RECEIPT_REQUIRED' })
  const stored = { requestId: receipt.requestId, consultaId: receipt.consultaId || null,
    instancia: receipt.instancia || null, state: receipt.state, stage: receipt.stage,
    startedAt: receipt.startedAt, updatedAt: receipt.updatedAt, targetConsultaId: receipt.targetConsultaId || null,
    reuseOnly: receipt.reuseOnly === true, linkedFromRequestId: receipt.linkedFromRequestId || null, manualLink: receipt.manualLink === true,
    ...(receipt.operation === 'REOBTER' ? { operation: 'REOBTER', anexoId: receipt.anexoId, versaoAnterior: receipt.versaoAnterior } : {}) }
  const raw = JSON.stringify(stored)
  try {
    storage.setItem(key, raw)
    if (storage.getItem(key) !== raw) throw new Error('Receipt readback mismatch')
  } catch {
    throw new ProcessDocumentSyncError('Não foi possível guardar e reler o recibo no navegador. A próxima etapa não foi enviada.', { code: 'LOCAL_RECEIPT_REQUIRED', receipt })
  }
  return stored
}

export function scopedProcessSyncReceiptWriter({ storage, actorId, numeroProcesso, initialReceipt, isCurrent, onCurrent, onStale }) {
  let previousKey = initialReceipt ? processSyncStorageKey(actorId, numeroProcesso, initialReceipt.instancia) : ''
  return (receipt) => {
    const current = isCurrent()
    const scoped = current ? receipt : { ...receipt, state: 'UNCERTAIN' }
    const key = processSyncStorageKey(actorId, numeroProcesso, scoped.instancia)
    const stored = writeProcessSyncReceipt(storage, key, scoped)
    if (previousKey.endsWith(':pendente') && previousKey !== key) storage.removeItem(previousKey)
    previousKey = key
    if (current) onCurrent(stored)
    else {
      onStale?.()
      throw new ProcessDocumentSyncError('A sessão ou o processo mudou. O recibo da etapa anterior foi mantido como incerto no escopo original.', { uncertain: true, receipt: stored })
    }
    return stored
  }
}

export async function runProcessDocumentSync({ api, input, previousReceipt = null, signal, onReceipt, onConsulta, onProgress, now = () => Date.now() }) {
  let current = null
  let receipt = previousReceipt || { requestId: input.requestId, instancia: input.instancia || null,
    consultaId: null, targetConsultaId: input.consultaId || null, reuseOnly: input.reuseOnly === true,
    state: 'REQUESTING', stage: 'INICIAR', startedAt: new Date(now()).toISOString() }
  const save = (changes) => { receipt = { ...receipt, ...changes, updatedAt: new Date(now()).toISOString() }; onReceipt(receipt) }
  const remote = async (stage, action, expected, expectedAttachment = null) => {
    save({ stage, state: 'REQUESTING' })
    let response
    try { response = await action() }
    catch { response = { success: false } }
    if (!signal?.aborted && !response?.success && stage === 'DOCUMENTO' && response?.code === 'JUDIT_LOCAL_EXTRACTION_FAILED' && current) {
      let local
      try { local = await api.get(current.consultaId, signal) } catch { local = null }
      if (!signal?.aborted && local?.success) {
        let confirmed
        try { confirmed = acceptJuditConsulta(local.data, expected) } catch { confirmed = null }
        if (confirmed && !processSyncUnknown(confirmed) && confirmed.anexos.some((row) => row.anexoId === expectedAttachment && row.estado === 'ERRO' && isGuid(row.documentoId))) {
          current = confirmed
          save({ state: 'KNOWN', consultaId: current.consultaId, instancia: current.instancia })
          onConsulta(current)
          return current
        }
      }
    }
    if (signal?.aborted || !response?.success) {
      const uncertain = signal?.aborted || juditUncertain(response) || response?.code === 'JUDIT_OPERATION_IN_PROGRESS'
      save({ state: uncertain ? 'UNCERTAIN' : 'FAILED' })
      throw new ProcessDocumentSyncError(processSyncError(response), { code: response?.code, uncertain, receipt })
    }
    let confirmed
    try { confirmed = acceptJuditConsulta(response.data, expected) }
    catch {
      save({ state: 'UNCERTAIN' })
      throw new ProcessDocumentSyncError('A resposta não corresponde ao processo, grau ou consulta solicitados. A operação foi mantida como incerta.', { uncertain: true, receipt })
    }
    current = confirmed
    save({ state: 'KNOWN', consultaId: current.consultaId, instancia: current.instancia,
      requestId: isGuid(current.requestIdLocal) ? current.requestIdLocal : receipt.requestId })
    onConsulta(current)
    return current
  }
  const expectedProcess = { numeroProcesso: input.numeroProcesso, ...(input.instancia ? { instancia: input.instancia } : {}) }
  if (previousReceipt) {
    if (['REQUESTING', 'UNCERTAIN'].includes(previousReceipt.state)) throw new ProcessDocumentSyncError('Reconcilie a operação local antes de continuar. Nenhuma chamada foi repetida.', { uncertain: true, receipt })
    const response = await api.get(previousReceipt.consultaId, signal)
    if (signal?.aborted || !response.success) throw new ProcessDocumentSyncError(processSyncError(response), { receipt })
    current = acceptJuditConsulta(response.data, { ...expectedProcess, consultaId: previousReceipt.consultaId })
    onConsulta(current)
  } else {
    onProgress(processSyncProgress(null, 'STARTING'))
    await remote('INICIAR', () => api.synchronizeProcess(input, signal), expectedProcess)
  }
  if (processSyncUnknown(current)) {
    save({ state: 'UNCERTAIN' })
    throw new ProcessDocumentSyncError('A consulta mantém uma operação sem resultado confirmado. Apenas releitura local está disponível; nenhum envio será repetido.', { uncertain: true, receipt })
  }
  if (juditRefreshWait(current, now()) === 0) {
    onProgress(processSyncProgress(current, 'REFRESHING'))
    await remote('ATUALIZAR', () => api.refresh(current.consultaId, current.versao, signal), { ...expectedProcess, consultaId: current.consultaId })
  }
  const attempted = new Set()
  while (attempted.size < PROCESS_SYNC_DOCUMENT_LIMIT) {
    const known = current.anexos.filter((row) => isGuid(row.documentoId)).length
    const next = current.anexos.find((row) => row.estado === 'DISPONIVEL' && row.podeBaixar && !attempted.has(row.anexoId))
    if (!next || known >= PROCESS_SYNC_DOCUMENT_LIMIT) break
    attempted.add(next.anexoId)
    onProgress(processSyncProgress(current, 'EXTRACTING', { documentTitle: next.titulo, attempted: attempted.size }))
    await remote('DOCUMENTO', () => api.download(current.consultaId, current.versao, next.anexoId, signal), { ...expectedProcess, consultaId: current.consultaId }, next.anexoId)
    if (processSyncUnknown(current)) {
      save({ state: 'UNCERTAIN' })
      throw new ProcessDocumentSyncError('A obtenção de um documento ainda não possui resultado confirmado. A sequência foi interrompida sem repetir a chamada.', { uncertain: true, receipt })
    }
  }
  const waiting = processSyncRemotePending(current)
  save({ state: waiting ? 'WAITING' : 'FINISHED' })
  onProgress(processSyncProgress(current, waiting ? 'WAITING' : 'FINISHED', { limitReached: current.anexos.filter((row) => isGuid(row.documentoId)).length >= PROCESS_SYNC_DOCUMENT_LIMIT,
    nextUpdateAt: current.proximaAtualizacaoEm }))
  return { consulta: current, receipt }
}

export async function runProcessDocumentReextraction({ api, consulta, anexo, requestId, previousReceipt, signal, onReceipt, onConsulta }) {
  if (previousReceipt && !['FINISHED', 'FAILED'].includes(previousReceipt.state) || processSyncUnknown(consulta)) {
    throw new ProcessDocumentSyncError('Reconcilie a operação em andamento antes de obter este anexo novamente. Nenhuma chamada foi enviada.', { uncertain: true, receipt: previousReceipt })
  }
  const confirmed = acceptJuditConsulta(consulta)
  if (confirmed.podeAlterar !== true || !anexo?.podeReextrair || !confirmed.anexos.some((row) => row.anexoId === anexo.anexoId && row.podeReextrair)) {
    throw new ProcessDocumentSyncError('Este anexo não está disponível para uma nova obtenção. Releia a lista local.')
  }
  let receipt = { requestId, consultaId: consulta.consultaId, instancia: consulta.instancia, operation: 'REOBTER',
    anexoId: anexo.anexoId, versaoAnterior: consulta.versao, state: 'REQUESTING', stage: 'REOBTER', startedAt: new Date().toISOString() }
  const save = (state) => { receipt = { ...receipt, state, updatedAt: new Date().toISOString() }; onReceipt(receipt) }
  save('REQUESTING')
  let response
  try { response = await api.reextract(consulta.consultaId, consulta.versao, anexo.anexoId, signal) }
  catch { response = { success: false } }
  if (!signal?.aborted && !response?.success && response?.code === 'JUDIT_LOCAL_EXTRACTION_FAILED') {
    try { response = await api.get(consulta.consultaId, signal) } catch { response = { success: false } }
  }
  if (signal?.aborted || !response?.success) {
    const uncertain = signal?.aborted || juditUncertain(response) || response?.code === 'JUDIT_OPERATION_IN_PROGRESS'
    save(uncertain ? 'UNCERTAIN' : 'FAILED')
    throw new ProcessDocumentSyncError(processSyncError(response), { code: response?.code, uncertain, receipt })
  }
  let result
  try {
    result = acceptJuditConsulta(response.data, consulta)
    if (processSyncUnknown(result) || result.versao === receipt.versaoAnterior || !result.anexos.some((row) => row.anexoId === anexo.anexoId &&
      ['EXTRAIDO', 'ERRO', 'OCR_NECESSARIO', 'LIMITE_EXCEDIDO'].includes(row.estado))) throw new Error('Unconfirmed result')
  } catch {
    save('UNCERTAIN')
    throw new ProcessDocumentSyncError('A nova obtenção deste anexo ainda não foi confirmada. Releia pelo recibo; nenhuma chamada será repetida automaticamente.', { uncertain: true, receipt })
  }
  save('FINISHED')
  onConsulta(result)
  return { consulta: result, receipt }
}

export async function reconcileProcessDocumentSync({ api, receipt, numeroProcesso, signal }) {
  let response = isGuid(receipt.consultaId) ? await api.get(receipt.consultaId, signal) : await api.getByRequest(receipt.requestId, signal)
  let fallback = false
  if (!response.success && response.status === 404 && receipt.reuseOnly && isGuid(receipt.targetConsultaId)) { response = await api.get(receipt.targetConsultaId, signal); fallback = true }
  if (signal?.aborted || !response.success) throw new ProcessDocumentSyncError('A operação ainda não foi vinculada a um recibo local confirmado. Nenhuma chamada à Judit foi repetida.', { uncertain: true, receipt })
  if (!receipt.consultaId && !fallback && response.data?.requestIdLocal !== receipt.requestId) throw new ProcessDocumentSyncError('O registro não confirma a identidade da solicitação local. A incerteza foi preservada.', { uncertain: true, receipt })
  const consulta = acceptJuditConsulta(response.data, { numeroProcesso, ...(receipt.instancia ? { instancia: receipt.instancia } : {}),
    ...(receipt.consultaId || fallback ? { consultaId: receipt.consultaId || receipt.targetConsultaId } : {}) })
  const retryUnconfirmed = receipt.operation === 'REOBTER' && (consulta.versao === receipt.versaoAnterior || !consulta.anexos.some((row) => row.anexoId === receipt.anexoId &&
    ['EXTRAIDO', 'ERRO', 'OCR_NECESSARIO', 'LIMITE_EXCEDIDO'].includes(row.estado)))
  const unknown = processSyncUnknown(consulta) || retryUnconfirmed
  return { consulta, receipt: { ...receipt, consultaId: consulta.consultaId, instancia: consulta.instancia,
    requestId: isGuid(consulta.requestIdLocal) ? consulta.requestIdLocal : receipt.requestId,
    state: unknown ? 'UNCERTAIN' : receipt.operation === 'REOBTER' ? 'FINISHED' : 'WAITING', stage: 'RECONCILIAR', updatedAt: new Date().toISOString() } }
}

export async function linkProcessDocumentSync({ api, receipt, selected, numeroProcesso, signal }) {
  if (!isGuid(selected?.consultaId) || ![1, 2].includes(receipt?.instancia) || selected.instancia !== receipt.instancia ||
    normalizeCnj(selected.numeroProcesso) !== normalizeCnj(numeroProcesso)) throw new ProcessDocumentSyncError('Escolha uma consulta local do mesmo processo e grau para vincular explicitamente o recibo.', { uncertain: true, receipt })
  const response = await api.get(selected.consultaId, signal)
  if (signal?.aborted || !response.success) throw new ProcessDocumentSyncError('Não foi possível reler a consulta selecionada. Nenhuma chamada à Judit foi feita.', { uncertain: true, receipt })
  const consulta = acceptJuditConsulta(response.data, { numeroProcesso, instancia: receipt.instancia, consultaId: selected.consultaId })
  return { consulta, receipt: { ...receipt, consultaId: consulta.consultaId,
    linkedFromRequestId: receipt.linkedFromRequestId || receipt.requestId, manualLink: true,
    requestId: isGuid(consulta.requestIdLocal) ? consulta.requestIdLocal : receipt.requestId,
    state: processSyncUnknown(consulta) ? 'UNCERTAIN' : 'WAITING', stage: 'VINCULAR_LOCAL', updatedAt: new Date().toISOString() } }
}
